import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Clock, Check, Loader, FileText, ShieldCheck, Search, Calendar, CalendarCheck, ChevronDown, ChevronUp } from "lucide-react";
import { createBulkStyles, BULK_CSS } from "./BulkActions/bulkActions.styles";
import { Btn } from "./BulkActions/BulkSteps";
import { getWorkflowApprovalLabel, getCurrentStepLabel } from "../Constant/EmployeeActions.config";

/**
 * Pending approvals — same shell as the Employee Action wizard: a header,
 * a toolbar (search + "awaiting me" filter) and one card per request with
 * its change summary, current approval step and approve / reject buttons.
 */
const PendingApprovalsModal = ({
  t,
  theme,
  isRTL,
  language,
  pendingActions,
  pendingWorkflows,
  isSubmitting,
  permissions,
  isAdminView,
  isAdminRole,
  ACTION_TYPES,
  profile,
  workflowDefinitions,
  // (action) => that request's company workflows; falls back to workflowDefinitions.
  definitionsForAction,
  highlightActionId = null,
  onApprove,
  onReject,
  onClose,
}) => {
  const s = useMemo(() => createBulkStyles(theme, isRTL), [theme, isRTL]);
  const k = s.k;
  const lang = language === "ar" ? "ar" : "en";
  const shellRef = useRef(null);
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Requests opened to show their reason / dates (the deep-linked one starts open).
  const [expanded, setExpanded] = useState(() => new Set(highlightActionId ? [String(highlightActionId)] : []));

  useEffect(() => {
    shellRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isSubmitting) setBusyId(null);
  }, [isSubmitting]);

  useEffect(() => {
    const onKey = (e) => {
      // The reject dialog (rendered on top) handles its own Escape.
      if (e.key === "Escape" && !isSubmitting && !document.querySelector("[data-reject-modal]")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isSubmitting, onClose]);

  // Notification deep-link: scroll the highlighted change into view.
  useEffect(() => {
    if (!highlightActionId) return;
    const timer = setTimeout(() => {
      document.getElementById(`pending-action-${highlightActionId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightActionId]);

  const normalizeApproverValue = (value) =>
    String(value || "").trim().toLowerCase();

  const currentUserCandidates = [
    profile?.infinityCode,
    profile?.employeeId,
    profile?.userId,
    profile?.code,
    localStorage.getItem("infinityCode"),
    localStorage.getItem("userInfinityCode"),
    localStorage.getItem("employeeCode"),
    sessionStorage.getItem("infinityCode"),
  ].filter(Boolean);
  const currentUserNormalized = currentUserCandidates.map(normalizeApproverValue);

  const currentAdminLevelIds = [
    profile?.adminLevelId,
    profile?.adminLevel?.id,
    profile?.admin_level_id,
    (() => {
      try {
        const raw = localStorage.getItem("adminLevel");
        if (raw) {
          const obj = JSON.parse(raw);
          return obj?.adminLevelId || obj?.admin_level_id || obj?.id || obj?.adminLevel?.id || null;
        }
      } catch {
        return null;
      }
    })(),
    (() => {
      try {
        const userStr =
          localStorage.getItem("user") || sessionStorage.getItem("user");
        const obj = userStr ? JSON.parse(userStr) : null;
        return obj?.adminLevelId || obj?.admin_level_id || obj?.adminLevel?.id || null;
      } catch {
        return null;
      }
    })(),
  ].filter(Boolean).map((value) => String(value).trim());

  const stepsFor = (action) =>
    pendingWorkflows?.[String(action.id)] ||
    pendingWorkflows?.[action.id] ||
    pendingWorkflows?.[String(action.workflowInstanceId)] ||
    pendingWorkflows?.[action.workflowInstanceId] ||
    [];

  const checkIsApprover = (action) => {
    const wfStepsForAction = stepsFor(action);

    const employeeManagerCodes = [
      action?.employee?.firstLineManagerInfinityCode,
      action?.employee?.secondLineManagerInfinityCode,
      action?.employee?.firstLineManager?.infinityCode,
      action?.employee?.secondLineManager?.infinityCode,
      action?.employee?.managerInfinityCode,
      action?.employee?.directManager,
      action?.employee?.lineManager,
    ].filter(Boolean).map(normalizeApproverValue);

    return Array.isArray(wfStepsForAction)
      ? wfStepsForAction.some((step) => {
        const stepStatus = String(step?.status || "").trim().toLowerCase();
        if (stepStatus !== "pending") return false;

        // Alternate approvers act exactly like the main assignee of the
        // equivalent type — any one of them showing up here means this user
        // can approve/reject the step. Each alternate is a FULL assignee-type
        // config resolved server-side to { assigneeType, assignedToInfinityCode,
        // assignedToAdminLevelId, assignedToAnyAdmin }, not a bare code, so
        // pull the relevant field out of each. alternateAssignees can arrive
        // as an array, null, or (defensively) a JSON string from the API.
        const resolvedAlternates = (() => {
          const raw = step?.alternateAssignees;
          if (Array.isArray(raw)) return raw;
          if (typeof raw === "string" && raw.trim()) {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })().filter((entry) => entry && typeof entry === "object");

        const alternateCodes = resolvedAlternates.map((alt) => alt.assignedToInfinityCode).filter(Boolean);
        const alternateAdminLevelIds = resolvedAlternates.map((alt) => alt.assignedToAdminLevelId).filter(Boolean);

        const assignedCodes = [
          step?.assignedToInfinityCode,
          step?.explicitAssigneeInfinityCode,
          step?.assignedTo,
          step?.assignedToId,
          step?.assignedToInfinity,
          step?.assigneeInfinityCode,
          step?.assignee?.infinityCode,
          step?.assignee?.code,
          ...alternateCodes,
        ].filter(Boolean).map(normalizeApproverValue);

        const isUserAssignee = assignedCodes.some((code) =>
          currentUserNormalized.includes(code)
        );
        const isAdminAssignee = Boolean(
          currentAdminLevelIds.length &&
          [step?.assignedToAdminLevelId, ...alternateAdminLevelIds].some(
            (levelId) => levelId && currentAdminLevelIds.includes(String(levelId).trim())
          )
        );
        const isEmployeeManager = employeeManagerCodes.some((code) =>
          currentUserNormalized.includes(code)
        );

        return isUserAssignee || isAdminAssignee || isEmployeeManager;
      })
      : false;
  };

  // Non-admins only see the requests waiting on them; admins see everything.
  const visiblePendingActions = pendingActions.filter((action) => {
    if (isAdminRole || isAdminView) return true;
    return checkIsApprover(action);
  });
  const seesAll = isAdminRole || isAdminView;

  const grantedFeatures = Array.isArray(permissions?.features?.changesPage)
    ? permissions.features.changesPage.map((f) => String(f || "").trim().toLowerCase())
    : [];
  const canApproveFeature = !isAdminView || isAdminRole || grantedFeatures.includes("approveactions");
  const canRejectFeature = !isAdminView || isAdminRole || grantedFeatures.includes("rejectactions");

  const employeeNameOf = (action) =>
    lang === "ar"
      ? action.employeeArabicName || action.employeeName || action.newData?.employeeName || t.notAvailable
      : action.employeeName || action.employeeArabicName || action.newData?.employeeName || t.notAvailable;
  const codeOf = (action) =>
    action.clientCode || action.employee?.clientCode || action.infinityCode || action.employeeId || "";

  const rows = visiblePendingActions.map((action) => ({ action, isApprover: checkIsApprover(action) }));
  const mineCount = rows.filter((r) => r.isApprover).length;
  const term = search.trim().toLowerCase();
  const shown = rows.filter(({ action, isApprover }) => {
    if (onlyMine && !isApprover) return false;
    if (!term) return true;
    return [employeeNameOf(action), action.employeeArabicName, action.employeeName, codeOf(action)]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(term));
  });

  const locale = lang === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const fmtDate = (v) => {
    if (!v) return t.notAvailable;
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? String(v)
      : new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(d);
  };
  const fmtNum = (v) => Number(v).toLocaleString(locale, { maximumFractionDigits: 2 });
  const arrow = isRTL ? "←" : "→";

  // ── One request ───────────────────────────────────────────────────────────
  const renderCard = ({ action, isApprover }) => {
    const actionConfig = ACTION_TYPES.find((a) => a.id === action.actionType);
    const ActionIcon = actionConfig?.icon || FileText;
    const isHighlighted = Number(highlightActionId) === Number(action.id);
    const actionDepartment =
      action?.employee?.department?.name || action?.oldDepartment || action?.newDepartment || null;

    const currentStepLabel = actionConfig ? getCurrentStepLabel(action, stepsFor(action), language, t) : null;
    const fullChainLabel = actionConfig
      ? getWorkflowApprovalLabel(actionConfig.id, definitionsForAction ? definitionsForAction(action) : workflowDefinitions, language, t, actionConfig.approvalLevelKey, actionDepartment)
      : t.notAvailable;
    const displayStepLabel = currentStepLabel || fullChainLabel;
    const hasFullChainDetail = Boolean(fullChainLabel) && fullChainLabel !== displayStepLabel;

    const changes = [];
    const oldD = action.oldData || {};
    const newD = action.newData || {};
    if (oldD.position && newD.position && oldD.position !== newD.position) changes.push([t.position, oldD.position, newD.position]);
    if (oldD.department && newD.department && oldD.department !== newD.department) changes.push([t.department, oldD.department, newD.department]);
    if (seesAll && oldD.salary != null && newD.salary != null && Number(oldD.salary) !== Number(newD.salary)) {
      changes.push([lang === "ar" ? "صافي الراتب" : "Net salary", fmtNum(oldD.salary), `${fmtNum(newD.salary)} ${t.egp}`, Number(newD.salary) > Number(oldD.salary)]);
    }

    const canAct = isApprover || isAdminRole;
    const busyHere = isSubmitting && busyId === action.id;
    const meta = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: k.muted, whiteSpace: "nowrap" };
    const neutralTag = {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
      color: k.text,
      background: k.subtle2,
      border: `1px solid ${k.border}`,
    };

    const idKey = String(action.id);
    const open = expanded.has(idKey);
    const toggle = () =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(idKey)) next.delete(idKey);
        else next.add(idKey);
        return next;
      });
    const Chevron = open ? ChevronUp : ChevronDown;

    return (
      <article
        key={action.id}
        id={`pending-action-${action.id}`}
        style={{
          borderRadius: 14,
          border: isHighlighted ? "2px solid #2e7cba" : `1px solid ${k.border}`,
          background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* Summary row — click to show the reason, dates and changes */}
        <div
          className="nx-pa-row"
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle();
            }
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                background: k.subtle2,
                border: `1px solid ${k.border}`,
                color: k.muted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ActionIcon size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {employeeNameOf(action)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 3 }}>
                <span style={neutralTag}>{actionConfig ? t[actionConfig.labelKey] : action.actionType}</span>
                {codeOf(action) && <span style={{ fontSize: 13, color: k.muted, fontWeight: 600 }}>{t.code} · {codeOf(action)}</span>}
              </div>
            </div>
          </div>

          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span
              style={{ display: "inline-flex", alignItems: "flex-start", gap: 6, fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 }}
              title={hasFullChainDetail ? `${t.fullChainLabel || (lang === "ar" ? "سلسلة الموافقات الكاملة" : "Full chain")}: ${fullChainLabel}` : undefined}
            >
              <ShieldCheck size={15} style={{ flexShrink: 0, marginTop: 2, color: k.muted }} />
              <bdi>{displayStepLabel}</bdi>
            </span>
            {isApprover && <span style={{ ...neutralTag, color: k.accent, background: k.accentSoft, borderColor: "transparent" }}>{t.yourTurn}</span>}
            {!isApprover && isAdminRole && <span style={{ ...neutralTag, color: k.muted }}>{t.adminOverride}</span>}
          </div>

          <div className="nx-pa-actions" style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
            {canAct && canApproveFeature && (
              <button
                type="button"
                className="nxb-btn"
                style={{ ...s.btnPrimary, height: 36, padding: "0 14px", ...(isSubmitting ? s.btnDisabled : null) }}
                disabled={isSubmitting}
                onClick={() => {
                  setBusyId(action.id);
                  onApprove(action);
                }}
              >
                {busyHere ? <Loader size={15} className="nxb-spin" /> : <Check size={15} />} {t.approve}
              </button>
            )}
            {canAct && canRejectFeature && (
              <button
                type="button"
                className="nxb-btn"
                style={{ ...s.btnSecondary, height: 36, padding: "0 14px", ...(isSubmitting ? s.btnDisabled : null) }}
                disabled={isSubmitting}
                onClick={() => onReject(action)}
              >
                <X size={15} /> {t.reject}
              </button>
            )}
          </div>

          <span style={{ color: k.muted, display: "flex" }} aria-hidden="true">
            <Chevron size={18} />
          </span>
        </div>

        {/* Details */}
        {open && (
          <div
            style={{
              borderTop: `1px solid ${k.border}`,
              background: k.subtle,
              padding: "14px 18px",
              display: "grid",
              gap: 10,
              animation: "nxbFade .2s ease",
            }}
          >
            <div style={{ display: "flex", gap: "6px 24px", flexWrap: "wrap" }}>
              <span style={meta}>
                <Calendar size={14} /> {t.requestedDate}: <strong style={{ color: k.text, fontWeight: 600 }}>{fmtDate(action.createdAt || action.requestedDate)}</strong>
              </span>
              <span style={meta}>
                <CalendarCheck size={14} /> {t.effectiveDate}: <strong style={{ color: k.text, fontWeight: 600 }}>{action.effectiveDate ? fmtDate(action.effectiveDate) : t.notAvailable}</strong>
              </span>
            </div>
            {changes.map(([label, from, to]) => (
              <div key={label} style={{ fontSize: 13.5 }}>
                <span style={{ color: k.muted }}>{label}: </span>
                <bdi style={{ color: k.muted }}>{from}</bdi> {arrow}{" "}
                <strong><bdi>{to}</bdi></strong>
              </div>
            ))}
            <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              <span style={{ color: k.muted }}>{t.reason}: </span>
              <bdi style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: action.reason ? k.text : k.muted }}>
                {action.reason || t.noDetailsProvided}
              </bdi>
            </div>
            {action.approverNotes && (
              <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                <span style={{ color: k.muted }}>{t.approverNotes}: </span>
                <bdi style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{action.approverNotes}</bdi>
              </div>
            )}
          </div>
        )}
      </article>
    );
  };

  return (
    <div
      className="nxb-overlay"
      style={{ ...s.overlay, zIndex: 1000, padding: "clamp(8px, 1.2vw, 16px)" }}
      onMouseDown={(e) => e.target === e.currentTarget && !isSubmitting && onClose()}
    >
      <style>{BULK_CSS}</style>
      <style>{`
        .nx-pa-row {
          display: grid; gap: 12px 20px; align-items: center; padding: 12px 16px; cursor: pointer;
          grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.3fr) auto 20px;
        }
        .nx-pa-row:hover { background: ${k.dark ? "rgba(46,124,186,0.08)" : "rgba(46,124,186,0.04)"}; }
        .nx-pa-row:focus-visible { outline: 3px solid rgba(46,124,186,.55); outline-offset: -3px; }
        .nx-pa-search:focus { border-color: #2e7cba !important; }
        @media (max-width: 1000px) {
          .nx-pa-row { grid-template-columns: minmax(0, 1fr) 20px; }
          .nx-pa-row > :nth-child(2), .nx-pa-actions { grid-column: 1 / -1; }
          .nx-pa-actions > button { flex: 1; }
        }
      `}</style>
      <div
        ref={shellRef}
        className="nxb-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-pa-title"
        tabIndex={-1}
        style={{
          ...s.shell,
          width: visiblePendingActions.length ? "min(1340px, 100%)" : "min(560px, 100%)",
          height: visiblePendingActions.length ? "min(820px, 95vh)" : "auto",
          position: "relative",
          outline: "none",
        }}
      >
        <div className="nxb-header" style={{ ...s.header, padding: "12px 28px", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
            <div style={{ ...s.headerIcon, width: 46, height: 46, borderRadius: 13 }}><Clock size={22} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 id="nx-pa-title" style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: "-0.01em" }}>{t.pendingApprovalsTitle}</h2>
              <p style={{ margin: "2px 0 0", fontSize: 14, color: k.muted }}>
                {visiblePendingActions.length} {t.actionsAwaiting}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="nxb-btn"
            style={s.iconButton}
            aria-label={t.close}
            title={t.close}
            disabled={isSubmitting}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {visiblePendingActions.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 28px", borderBottom: `1px solid ${k.border}`, background: k.subtle }}>
            <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 420 }}>
              <Search size={16} style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", insetInlineStart: 12, color: k.muted }} />
              <input
                className="nx-pa-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.pendingSearch}
                style={s.search}
              />
            </div>
            {seesAll && (
              <div style={s.segmented} role="radiogroup">
                {[
                  [false, t.pendingFilterAll, visiblePendingActions.length],
                  [true, t.pendingFilterMine, mineCount],
                ].map(([val, label, n]) => (
                  <button
                    key={String(val)}
                    type="button"
                    role="radio"
                    aria-checked={onlyMine === val}
                    className="nxb-seg"
                    style={{ ...s.segment(onlyMine === val), padding: "7px 14px", fontSize: 13.5 }}
                    onClick={() => setOnlyMine(val)}
                  >
                    {label}
                    <span style={{ fontSize: 12, color: k.muted, fontWeight: 700 }}>({n})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="nxb-body" style={{ ...s.body, padding: "18px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
          {visiblePendingActions.length === 0 ? (
            <div style={{ margin: "auto", textAlign: "center", padding: 24 }}>
              <div style={{ ...s.dropIcon, background: "rgba(16,185,129,0.12)", color: "#10b981" }}><Check size={30} /></div>
              <h3 style={{ margin: "0 0 6px", fontSize: 19, fontWeight: 800 }}>{t.noPendingApprovals}</h3>
              <p style={{ margin: 0, fontSize: 14, color: k.muted }}>{t.allActionsReviewed}</p>
            </div>
          ) : shown.length === 0 ? (
            <div style={{ margin: "auto", textAlign: "center", padding: 24, color: k.muted, fontSize: 14 }}>
              {t.noEmployeesMatch}
            </div>
          ) : (
            shown.map(renderCard)
          )}
        </div>

        <div className="nxb-footer" style={{ ...s.footer, padding: "12px 28px" }}>
          <span style={{ fontSize: 13.5, color: k.muted }}>
            {seesAll && mineCount > 0 && (
              <>
                <strong style={{ color: k.text }}>{mineCount}</strong> {t.pendingFilterMine}
              </>
            )}
          </span>
          <Btn s={s} variant="secondary" disabled={isSubmitting} onClick={onClose}>{t.close}</Btn>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalsModal;
